/**
 * 상세 테이블 핸들러 공통 훅 (새로운 타입 시스템)
 * 셀 값 변경, 편집 종료, 코드 검색 모달 설정 등의 공통 기능 제공
 * DetailBaseDto 대신 DetailTableCore & DetailTableState 사용으로 타입 제약 완화
 */

import { useCallback, MutableRefObject } from "react";
import { AgGridReact } from "ag-grid-react";
import { CellValueChangedEvent, CellEditingStoppedEvent } from "ag-grid-community";
import { showWarning } from "src/utils/toast/toastUtil";
import { DetailTableCore, DetailTableState, CalculateAmountFunction, CheckDuplicateFunction } from "src/types/detail-table-new";
import { 
  DetailTableFieldConfig, 
  CodeSearchFieldConfig
} from "src/types/detail-table";
import { CodeSearchConfig } from "src/types/code-search";
import { focusToCell } from "src/utils/grid/GridHandlerContent";

interface UseDetailTableHandlersProps<T extends DetailTableCore & DetailTableState> {
  /** 상세 데이터 목록 */
  detailList: T[];
  /** 상세 데이터 업데이트 함수 */
  updateDetailList: (index: number, data: T) => void;
  /** 원본 상세 데이터 목록 */
  originalDetailList: T[];
  /** 코드 검색 모달 설정 함수 */
  setCodeSearchModalProps: (props: any) => void;
  /** 그리드 레퍼런스 */
  gridRef: MutableRefObject<AgGridReact<T> | null>;
  
  /** 필드 설정 */
  fieldConfig: DetailTableFieldConfig;
  /** 코드 검색 설정 */
  codeSearchConfig: CodeSearchConfig;
  /** 금액 계산 함수 */
  calculateAmount: CalculateAmountFunction<T>;
  /** 모달 옵션 (선택적) - handleFocusId를 키로 하는 모달 크기 설정 */
  modalOptions?: Record<string, any>;
}

export function useDetailTableHandlers<T extends DetailTableCore & DetailTableState>({
  detailList,
  updateDetailList,
  originalDetailList,
  setCodeSearchModalProps,
  gridRef,
  fieldConfig,
  codeSearchConfig,
  calculateAmount,
  modalOptions,
}: UseDetailTableHandlersProps<T>) {

  /**
   * 범용 코드 검색 처리 함수
   */
  const handleCodeFieldChange = useCallback(async (
    field: string,
    newValue: any,
    oldValue: any,
    data: T,
    codeSearchConfig: CodeSearchFieldConfig
  ) => {
    if (newValue === oldValue) return;
    if (!newValue || !newValue.toString().trim()) {
      if (codeSearchConfig.clearFields) {
        updateDetailList(data.valueIndex, {
          ...data,
          ...codeSearchConfig.clearFields,
          isModified: data.inserted ? false : true,
        } as T);
      }
      return;
    }
    // 팝업/검색 가드: canOpenModal이 false를 반환하면 즉시 차단
    if (codeSearchConfig.canOpenModal && !codeSearchConfig.canOpenModal(data)) {
      return;
    }
    
    const inputCode = newValue.toString().trim().toUpperCase();
    
    // 중복 체크 (설정된 경우만)
    if (codeSearchConfig.duplicateCheck) {
      if ((codeSearchConfig.duplicateCheck as CheckDuplicateFunction<T>)(detailList, inputCode, data.valueIndex)) {
        showWarning(`경고: ${codeSearchConfig.category} '${newValue}'가 이미 존재합니다.`);
        const originalData = originalDetailList.find(
          (item) => item.valueIndex === data.valueIndex
        );
        if (originalData) {
          updateDetailList(data.valueIndex, {
            ...originalData,
            isModified: false,
          });
        }
        return;
      }
    }
    
    // 코드 검색 (설정된 경우만)
    if (codeSearchConfig.searchFunction) {
      try {
        const foundItem = await codeSearchConfig.searchFunction(inputCode, (item) => {
          const updatedData = { ...data };
          
          // 응답 데이터 매핑
          Object.entries(codeSearchConfig.responseMapping).forEach(([responseKey, fieldName]) => {
            (updatedData as any)[fieldName] = item[responseKey];
          });
          
          (updatedData as any).isModified = data.inserted ? false : true;
          updateDetailList(data.valueIndex,  calculateAmount(updatedData));
          
          // 자동 검색 성공 시 포커스 이동 (nextFocusCell이 설정된 경우)
          if (codeSearchConfig.nextFocusCell && gridRef.current) {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                focusToCell<T>(gridRef, data.valueIndex, codeSearchConfig.nextFocusCell!);
              });
            });
          }
        });
        
        // 찾지 못한 경우 모달로 검색
        if (!foundItem) {
          settingCodeSearchModalProps(
            codeSearchConfig.category,
            newValue.toString(),
            false,
            codeSearchConfig.codeField,
            data,
            data.valueIndex
          );
        }
      } catch (error) {
        settingCodeSearchModalProps(
          codeSearchConfig.category,
          newValue.toString(),
          false,
          codeSearchConfig.codeField,
          data,
          data.valueIndex
        );
      }
    }
  }, [detailList, originalDetailList, updateDetailList, gridRef]);

  /**
   * 코드 검색 모달 설정 함수
   */
  const settingCodeSearchModalProps = useCallback((
    category: string,
    search: string,
    isInfo: boolean,
    handleFocusId: string,
    originalData: T,
    valueIndex: number
  ) => {
    // 해당 필드의 설정 찾기
    const codeFieldConfig = Object.values(fieldConfig.codeSearchFields || {})
      .find(config => config.codeField === handleFocusId);
      
    if (!codeFieldConfig) {
      console.warn(`코드 검색 설정을 찾을 수 없습니다: ${handleFocusId}`);
      return;
    }
    // 팝업 가드: canOpenModal이 false를 반환하면 모달을 열지 않음
    if (codeFieldConfig.canOpenModal && !codeFieldConfig.canOpenModal(originalData)) {
      return;
    }

    // wrapperOption 생성: modalOptions가 있으면 사용, 없으면 undefined
    const wrapperOption = modalOptions?.[handleFocusId] 
      ? { ...modalOptions[handleFocusId], overlay: true }
      : undefined;

    const baseProps = {
      category,
      search,
      formName: codeSearchConfig.formName,
      isInfo,
      handleFocusId,
      onClear: () => {
        const clearedData = {
          ...originalData,
          ...codeFieldConfig.clearFields,
          isModified: originalData.inserted ? false : true,
        } as T;
        
        updateDetailList(valueIndex, clearedData);
      },
      onConfirm: (data: any, event?: any) => {
        const updatedData = { ...originalData };
        
        // 범용 응답 매핑
        Object.entries(codeFieldConfig.responseMapping).forEach(([responseKey, fieldName]) => {
          (updatedData as any)[fieldName] = data[responseKey];
        });
        
        (updatedData as any).isModified = originalData.inserted ? false : true;
        updateDetailList(valueIndex, updatedData);
        
        // 포커스 이동 처리
        if (codeSearchConfig.focusMoveConfig) {
          // focusMoveConfig가 있으면 모달에서 처리
        } else if (codeFieldConfig.nextFocusCell) {
          // nextFocusCell이 설정된 경우 해당 셀로 포커스 이동
          setTimeout(() => {
            if (gridRef.current) {
              focusToCell<T>(gridRef, valueIndex, codeFieldConfig.nextFocusCell!);
            }
          }, 100);
        } else if (codeSearchConfig.useLegacyFocus) {
          if (!event || (event && (event.detail === 0 || event.detail === 2))) {
            setTimeout(() => {
              const api = gridRef.current?.api;
              if (!api) return;
              
              let targetRowIndex = -1;
              api.forEachNode((node: any) => {
                if (node.data && node.data.valueIndex === valueIndex && node.rowIndex !== null) {
                  targetRowIndex = node.rowIndex;
                }
              });
              
              if (targetRowIndex !== -1) {
                api.startEditingCell({
                  rowIndex: targetRowIndex,
                  colKey: fieldConfig.qtyField,
                });
              }
            }, 100);
          }
        }
      },
      ...(codeFieldConfig.searchFilters && { extraFilter: codeFieldConfig.searchFilters }),
      ...(wrapperOption && { wrapperOption }), // wrapperOption이 있을 때만 추가
    };  

    if (codeSearchConfig.focusMoveConfig) {
      return setCodeSearchModalProps({
        ...baseProps,
        rowIndex: valueIndex,
        gridRef: gridRef,
        focusMoveConfig: codeSearchConfig.focusMoveConfig,
      });
    }

    return setCodeSearchModalProps(baseProps);
  }, [
    detailList,
    updateDetailList,
    fieldConfig,
    codeSearchConfig,
    gridRef,
    setCodeSearchModalProps
  ]);

  /**
   * 셀 값 변경 핸들러
   */
  const handleCellChange = useCallback(
    async (event: CellValueChangedEvent<T>) => {
      const { data, colDef, newValue, oldValue } = event;
      if (!data || !colDef.field) return;
      const field = colDef.field as keyof T;

      // 범용 코드 검색 처리
      const codeFieldConfig = fieldConfig.codeSearchFields?.[field as string];
      if (codeFieldConfig) {
        await handleCodeFieldChange(field as string, newValue, oldValue, data, codeFieldConfig);
        return;
      }

      // 수량 변경 처리
      if (field === fieldConfig.qtyField) {
        const latestRow = detailList.find(
          (item) => item.valueIndex === data.valueIndex
        ) || data;
        const nextRow = { ...latestRow, [field]: newValue };
        
        setTimeout(() => {
          updateDetailList(data.valueIndex, {
            ...calculateAmount(nextRow),
            isModified: data.inserted ? false : true,
          });
        }, 0);
        return;
      }

      // 기타 필드 변경 처리
      const latestRow = detailList.find(
        (item) => item.valueIndex === data.valueIndex
      ) || data;
      const nextRow = { ...latestRow, [field]: newValue };
      
      updateDetailList(data.valueIndex, {
        ...calculateAmount(nextRow),
        isModified: data.inserted ? false : true,
      });
    }, [
      detailList,
      updateDetailList,
      originalDetailList,
      fieldConfig,
      handleCodeFieldChange,
      settingCodeSearchModalProps,
      calculateAmount,
    ]
  );

  /**
   * 셀 편집 종료 핸들러
   */
  const handleCellEditingStopped = useCallback(
    (event: CellEditingStoppedEvent<T>) => {
      const { data, colDef } = event;
      if (!data || !colDef.field) return;
      const field = colDef.field as keyof T;
      
      if (field === fieldConfig.qtyField) {
        // 수량 셀 편집 종료 시에도 금액을 강제로 계산
        const latestRow = detailList.find(
          (item) => item.valueIndex === data.valueIndex
        ) || data;
        const originalRow = originalDetailList.find((item) => item.valueIndex === data.valueIndex);
        const normalize = (v: any) => (v === null || v === undefined || v === '' ? null : Number(v));
        const latestValue = normalize((latestRow as any)[field]);
        const originalValue = normalize(originalRow ? (originalRow as any)[field] : undefined);
        const hasChanged = originalRow ? latestValue !== originalValue : false;

        const nextRowData = {
          ...calculateAmount(latestRow),
        } as T;
        (nextRowData as any).isModified = data.isModified || (!data.inserted && hasChanged);
        
        updateDetailList(data.valueIndex, nextRowData);
        return;
      }
      // 필요시 다른 컬럼에 대한 추가 로직 작성
    },
    [detailList, originalDetailList, updateDetailList, fieldConfig, calculateAmount]
  );

  return {
    handleCellChange,
    handleCellEditingStopped,
    settingCodeSearchModalProps,
  };
}
